<template>
  <div ref="viewerContainer" class="pdf-viewer-container"></div>
</template>

<script>
import { getPdf } from '../store/actions'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { PDFPageView, DefaultTextLayerFactory, DefaultAnnotationLayerFactory, EventBus } from 'pdfjs-dist/legacy/web/pdf_viewer';
import 'pdfjs-dist/legacy/web/pdf_viewer.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/app.worker.js';

export default {
  name: 'PdfViewer',

  props: {
    src: String,
    scale: { type: Number, default: 0.74 },
    searchString: {
      type: String
    },
    pdfDbEntryKey: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      pdfDoc: null,
      pages: [],
      matches: [],
      currentMatch: -1,
      resizeObserver: null,
      searchTimeout: null,
      initialRenderDone: false,
      containerWidth: 0
    };
  },
  watch: {
    searchString(newStr) {
      console.log(newStr)
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(() => {
        this.highlightMatches(newStr);
      }, 150);
    }
  },
  async mounted() {
    try {
      let loadingTask;
      if (this.pdfDbEntryKey) {
        await getPdf(this.$store, this.pdfDbEntryKey);
        const pdf = this.$store.getters.pdf;
        const typedArray = this.base64ToTypedArray(pdf.base64Representation);
        loadingTask = pdfjsLib.getDocument(typedArray);
      } else if (this.src) {
        loadingTask = pdfjsLib.getDocument(this.src);
      }

      this.pdfDoc = await loadingTask.promise;
      const container = this.$refs.viewerContainer;

      for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
        const page = await this.pdfDoc.getPage(pageNum);

        const pageDiv = document.createElement('div');
        pageDiv.className = 'pdf-page-wrapper';
        container.appendChild(pageDiv);

        const eventBus = new EventBus();

        const scale = this.getResponsiveScale(page);
        const viewport = page.getViewport({ scale });

        const pageView = new PDFPageView({
          container: pageDiv,
          id: pageNum,
          scale,
          defaultViewport: viewport,
          eventBus,
          textLayerFactory: new DefaultTextLayerFactory(),
          annotationLayerFactory: new DefaultAnnotationLayerFactory(),
        });

        eventBus.on('textlayerrendered', () => {
          if (!pageView.__textReady) {
            pageView.__textReady = true;

            if (this.pages.every(p => p.__textReady)) {
              this.initialRenderDone = true;

              if (this.searchString) {
                this.highlightMatches(this.searchString);
              }
            }
          }
        });

        pageView.setPdfPage(page);
        await pageView.draw();

        this.pages.push(pageView);
      }

      this.containerWidth = this.$refs.viewerContainer.clientWidth;

      this.resizeObserver = new ResizeObserver(entries => {
        const newWidth = entries[0].contentRect.width;

        if (!this.initialRenderDone) return;

        if (Math.abs(newWidth - this.containerWidth) < 2) return;

        this.containerWidth = newWidth;
        this.rescalePages();
      });

      this.resizeObserver.observe(this.$refs.viewerContainer);

    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    base64ToTypedArray (base64Str) {
      const raw = atob(base64Str)
      const uint8Array = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) {
        uint8Array[i] = raw.charCodeAt(i)
      }
      return uint8Array
    },

    highlightMatches(searchStr) {

      // ALWAYS clear old highlights first
      this.pages.forEach(pageView => {
        const textDivs =
          (pageView.textLayer && pageView.textLayer.textDivs) || [];

        textDivs.forEach(div => {
          div.innerHTML = div.textContent;
        });
      });

      this.matches = [];
      this.currentMatch = -1;

      if (!searchStr || !searchStr.trim()) return;

      const escapedStr = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedStr, 'gi');

      this.pages.forEach(pageView => {
        if (!pageView.textLayer || !pageView.textLayer.textDivs) return;

        const textDivs = [...pageView.textLayer.textDivs];

        textDivs.sort((a, b) => {
          const dy = a.offsetTop - b.offsetTop;
          if (Math.abs(dy) > 2) return dy;
          return a.offsetLeft - b.offsetLeft;
        });

        // Clear old highlights
        textDivs.forEach(div => {
          div.innerHTML = div.textContent;
        });

        let fullText = '';
        const offsets = [];

        textDivs.forEach(div => {
          const start = fullText.length;
          fullText += div.textContent;
          offsets.push({ div, start, end: fullText.length });
        });

        let match;
        while ((match = regex.exec(fullText)) !== null) {
          const matchStart = match.index;
          const matchEnd = match.index + match[0].length;

          offsets.forEach(({ div, start, end }) => {
            if (matchEnd <= start || matchStart >= end) return;

            const relativeStart = Math.max(0, matchStart - start);
            const relativeEnd = Math.min(end - start, matchEnd - start);

            const original = div.textContent;
            div.innerHTML = '';

            if (relativeStart > 0) {
              div.appendChild(document.createTextNode(original.slice(0, relativeStart)));
            }

            const mark = document.createElement('mark');
            mark.textContent = original.slice(relativeStart, relativeEnd);
            div.appendChild(mark);
            this.matches.push({ pageView, div: mark });

            if (relativeEnd < original.length) {
              div.appendChild(document.createTextNode(original.slice(relativeEnd)));
            }
          });
        }
      });
    },

    gotoMatch(direction) {
      if (!this.matches.length) return;

      this.currentMatch += direction;

      // Wrap around
      if (this.currentMatch >= this.matches.length) this.currentMatch = 0;
      if (this.currentMatch < 0) this.currentMatch = this.matches.length - 1;

      const match = this.matches[this.currentMatch];
      if (match && match.div) {
        match.div.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.matches.forEach((m, i) => m.div.classList.toggle('current', i === this.currentMatch));
      }
    },

    getResponsiveScale(page) {
      const container = this.$refs.viewerContainer;
      const containerWidth = container.clientWidth;

      const unscaledViewport = page.getViewport({ scale: 1 });
      return (containerWidth / unscaledViewport.width) * this.scale;
    },

    async rescalePages() {
      for (const pageView of this.pages) {
        const page = pageView.pdfPage;
        const scale = this.getResponsiveScale(page);

        // Clear existing content
        pageView.reset();

        pageView.scale = scale;
        pageView.viewport = page.getViewport({ scale });

        // FULL redraw (canvas + text layer)
        await pageView.draw();
      }

      if (this.searchString) {
        this.highlightMatches(this.searchString);
      }
    }

  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
};
</script>

<style>
.pdf-viewer-container {
  position: relative;
  /* widt100%; */
  /* max-width: 100vw; */
  overflow-x: hidden;
}
.pdf-page-wrapper {
  position: relative;
  margin-bottom: 10px;
}
mark.current {
  background-color: #0000ff;
  color: transparent;
}
mark {
  background-color: yellow;
  color: transparent;
}
</style>
